/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Alert, Checkbox, Icon, Input, message, Modal, Select, Upload } from 'antd';
import XLSX from 'xlsx';
import { get, keyBy, omit, set, size, upperFirst } from 'lodash';
import { useState, useEffect } from 'react';
import { getKeyMaps, getRawLicense, rowMatchesSearch, withSorters } from '../../actions/utils';
import { EditableFormTable } from '../../_shared/editable-table';
import { TableListHeader } from '../../_shared/table-list';
import { getStoredLicenseNumbers, instance, updateStoredLicenseNumbers } from '../../_shared/services/api.service';
import useDebounce from '../../_shared/hooks/useDebounce';
import moment from 'moment';
import Row from '../../_shared/row';
import { Button } from '../../_shared/button';
import { getRoutes } from '../../_shared/services/routes.service';
import { Days } from '../drive-schedule/schedule';

const days = [{ title: 'All', value: 'all' }, ...Object.keys(Days).map(k => ({ title: Days[k], value: k }))];

const timeformat = 'h:mm a';

const defaultWindows = [
  [moment('9:00', timeformat), moment('17:00', timeformat)],
  [moment('9:00', timeformat), moment('17:00', timeformat)],
  [moment('9:00', timeformat), moment('17:00', timeformat)],
  [moment('9:00', timeformat), moment('17:00', timeformat)],
  [moment('9:00', timeformat), moment('17:00', timeformat)],
  [],
  [],
]

const BILLING_OPTS = [
  { title: 'Box Count', value: 'box_count', inputProps: {} },
  { 
    title: 'Payment Fees Daily', 
    value: 'pay_fees_daily', 
    inputProps: { 
      type: 'select', 
      mode: "multiple",
      opts: days,
    } 
  },
  { 
    title: 'Payment Fees Weekly', 
    value: 'pay_fees_weekly', 
  },
  { 
    title: 'Pickup Fees Daily', 
    value: 'pickup_fees_daily', 
    inputProps: { 
      type: 'select', 
      mode: "multiple",
      opts: days,
    } 
  },
  { 
    title: 'Pickup Fees Weekly', 
    value: 'pickup_fees_weekly', 
  },
]

export const AddSelectBilling = props => {
  const [state, setState] = useState({
    items: Array.isArray(props.value) ? props.value : [],
  })

  const options = size(props.options) ? props.options : BILLING_OPTS;

  const isViewOnly = get(props, 'viewOnly', false);

  // Always sync local items with props.value, including the empty/undefined case —
  // otherwise switching to a vendor that has no billing items leaves the previous
  // vendor's items visible and edits get applied to the wrong vendor.
  useEffect(() => {
    setState(s => ({ ...s, items: Array.isArray(props.value) ? props.value : [] }))
  }, [props.value]);

  const filterOption = (val, opt) => {
    const v = (get(opt, 'props.children', '') || '').toLowerCase();
    return v.includes((val || '').toLowerCase());
  }

  const renderInput = (item, index, onChange) => {
    const inputProps = get(options.find(o => o.value == item.key), `inputProps`, {});
    
    if (inputProps.type === 'select') {
      return (
        <Select style={{ width: '50%', minWidth: 180 }} loading={props.loading} {...inputProps} disabled={isViewOnly} value={item.value} onChange={e => onChange(e, 'value')}>
          {(inputProps.opts || []).map((o, i) => <Select.Option value={o.value}>{o.title}</Select.Option>)}
        </Select>
      )
    }

    return <Input style={{ width: '50%' }} loading={props.loading} {...inputProps} disabled={isViewOnly} value={item.value} onChange={e => onChange(e.target.value, 'value')}  />
  }

  const renderItem = (item, index) => {
    const onChange = (e, key) => {
      const items = [...state.items];
      set(items, [index, key], e);
      setState(s => ({ ...s, items }));
      if (props.onChange) {
        props.onChange(items, index);
      } else {
        props.setFieldsValue({
          [props.dataIndex]: items,
        })
      }
    }

    return (
      <Row key={index}>
        <Select loading={props.loading} showSearch filterOption={filterOption} disabled={isViewOnly} dropdownMatchSelectWidth={false} style={{ width: '50%', minWidth: 180 }} value={item.key} onChange={e => onChange(e, 'key')}>
          {options.map((opt, i) => <Select.Option key={i} value={opt.value}>{opt.title}</Select.Option>)}  
        </Select>
        {renderInput(item, index, onChange)}
        {!isViewOnly && <Button onClick={() => deleteItem(index)} icon={'delete'} />}
      </Row>
    )
  }

  const deleteItem = (i) => {
    const items = size(state.items) <= 1 ? [] : state.items.filter((f, ii) => ii != i);
    if (props.onChange) {
      props.onChange(items, i);
    } else {
      props.setFieldsValue({
        [props.dataIndex]: items,
      })
    }
    setState(s => ({ ...s, items: [...items] }))
  }

  const addItem = () => {
    setState(s => ({ ...s, items: [...s.items, { }] }))
  }

  return (
    <div>
      {state.items.map(renderItem)}
      {!isViewOnly && <Row>
        <Button icon="plus" onClick={addItem} title="Add Billing Item" />
        {props.renderFooter && props.renderFooter()}
      </Row>}
    </div>
  )
}

const AddSelect = props => {
  const [state, setState] = useState({
    items: [],
  })

  const isViewOnly = get(props, 'viewOnly', false);

  useEffect(() => {
    if (props.value && Array.isArray(props.value)) {
      setState(s => ({ ...s, items: props.value }))
    }
  }, [props.value]);

  const filterOption = (val, opt) => {
    const v = (get(opt, 'props.children', '') || '').toLowerCase();
    return v.includes((val || '').toLowerCase());
  }

  const renderItem = (item, index) => {
    const onChange = (e, key) => {
      const items = [...state.items];
      set(items, [index, key], e);
      setState(s => ({ ...s, items }));
      props.setFieldsValue({
        [props.dataIndex]: items,
      })
    }

    return (
      <Row>
        <Select showSearch filterOption={filterOption} disabled={isViewOnly} style={{ width: '75%' }} value={item.route} onChange={e => onChange(e, 'route')}>
          {props.options.map((opt, i) => <Select.Option key={i} value={opt.value}>{opt.title}</Select.Option>)}  
        </Select>
        <Select disabled={isViewOnly} style={{ width: '25%' }}  value={item.day} onChange={e => onChange(e, 'day')}>
          {props.days.map((opt, i) => <Select.Option key={i} value={opt.value}>{upperFirst(opt.title)}</Select.Option>)}  
        </Select>
        {!isViewOnly && <Button onClick={() => deleteItem(index)} icon={'delete'} />}
      </Row>
    )
  }

  const deleteItem = (i) => {
    const items = size(state.items) <= 1 ? [] : state.items.filter((f, ii) => ii != i);
    props.setFieldsValue({
      [props.dataIndex]: items,
    })
    setState(s => ({ ...s, items: [...items] }))
  }

  const addItem = () => {
    setState(s => ({ ...s, items: [...s.items, { day: 'all', route: null }] }))
  }

  return (
    <div>
      {state.items.map(renderItem)}
      {!isViewOnly && <Row>
        <Button icon="plus" onClick={addItem} title="Add New Item" />
      </Row>}
    </div>
  )
}

// Stamped by the license scrape (server/src/controllers/licensenumbers/common.js —
// applyStateListPresence) on the licensenumbers blob, and owned by it: _notOnStateList
// /_missingSince appear when a license the state list used to carry stops showing up
// and are cleared the next time it does, _stateSeen marks a license the scrape has ever
// seen. A grid row is a blob row merged with its lnstored override, so saving an edit
// must NOT carry these into lnstored: lnstored is spread OVER the blob on every read,
// and the flag lifecycle only ever rewrites the blob, so a copy here would shadow it
// permanently. Mirror of SCRAPE_OWNED_KEYS in that same common.js, which strips them
// again on the way into the database.
const SCRAPE_OWNED_KEYS = ['_stateSeen', '_notOnStateList', '_missingSince'];

// "License Override" (card 286). The opposite of the keys above: USER-owned, so it is
// saved into lnstored like emails/billing/active and the scrape can never clear it.
// While the flag is on, the license scrape stops refreshing the five fields below on
// that license — mirrored by name on the server as LICENSE_OVERRIDE_KEY /
// LICENSE_OVERRIDE_PROTECTED in server/src/controllers/licensenumbers/common.js
// (separate CRA project, so there is no shared module to import).
//
// Everything else the scrape owns (DateUpdated, FacilityType, addr_*, sec_lic_status)
// still refreshes on a locked license, so it keeps a current scrape stamp and stays
// inside the "N licenses retrieved" count in the banner.
const LICENSE_OVERRIDE_KEY = 'lockOverride';
const LOCK_PROTECTED_FIELDS = ['Licensee', 'DBA', 'Street Address', 'City', 'Zip'];

const overrideLabel = record => (get(record, LICENSE_OVERRIDE_KEY) ? 'Locked' : 'Unlocked');

// State List column text. A manually added license has never been seen by the scrape,
// so it reads blank rather than claiming to be listed.
const stateListLabel = record => {
  if (get(record, '_notOnStateList')) {
    const since = moment(get(record, '_missingSince'), 'YYYY-MM-DD');
    return `Not listed${since.isValid() ? ` (since ${since.format('M/D/YYYY')})` : ''}`;
  }
  return get(record, '_stateSeen') ? 'Listed' : '';
}

// Pulled out of the Receiving Windows column so both its cell renderer and its
// searchValue can read the day labels without rebuilding the whole COLS() array
// (the renderer used to call COLS() once per day, per row).
const WINDOW_OPTIONS = [
  { label: 'Mon', format: timeformat, defaultValue: [moment('9:00', timeformat), moment('17:00', timeformat)] },
  { label: 'Tues', format: timeformat, defaultValue: [moment('9:00', timeformat), moment('17:00', timeformat)] },
  { label: 'Wed', format: timeformat, defaultValue: [moment('9:00', timeformat), moment('17:00', timeformat)] },
  { label: 'Thurs', format: timeformat, defaultValue: [moment('9:00', timeformat), moment('17:00', timeformat)] },
  { label: 'Fri', format: timeformat, defaultValue: [moment('9:00', timeformat), moment('17:00', timeformat)] },
  { label: 'Sat', format: timeformat, defaultValue: [] },
  { label: 'Sun', format: timeformat, defaultValue: [] },
];

// Global search and per-column sorting (card 286) now live in actions/utils, because card
// #277 gives every table page the same box and they all have to agree on what a cell's
// searchable text is. The behaviour here is unchanged — `searchValue` still wins over
// `exportValue` over the raw dataIndex value — except that the two checkbox columns
// (Active, Man. Req) no longer answer to a substring of "true"/"false".

const COLS = (opts = {}) => ([
  {
    title: 'LN',
    dataIndex: 'License #',
    editable: true,
    width: 200,
  },
  {
    title: 'Name',
    dataIndex: 'Licensee',
    editable: true,
    width: 200,
  },
  {
    title: 'DBA',
    dataIndex: 'DBA',
    editable: true,
    required: false,
  },
  {
    title: 'Street Address',
    dataIndex: 'Street Address',
    editable: true,
  },
  {
    title: 'City',
    dataIndex: 'City',
    editable: true,
  },
  {
    title: 'Zip',
    dataIndex: 'Zip',
    editable: true,
  },
  {
    title: 'Emails',
    dataIndex: 'emails',
    editable: true,
    inputType: 'textarea',
    required: false,
  },
  {
    title: 'Billing',
    dataIndex: 'billing',
    editable: true,
    days,
    width: 400,
    required: false,
    customInput: p => <AddSelectBilling {...p} />,
    // Search/sort on what the cell shows (the option title), not on the raw
    // `[{key,value}]` shape.
    searchValue: record => (get(record, 'billing') || []).map(b => {
      const title = get(BILLING_OPTS.find(o => o.value === get(b, 'key')), 'title', get(b, 'key', ''));
      const val = get(b, 'value');
      return `${title} ${Array.isArray(val) ? val.join(' ') : (val === null || val === undefined ? '' : val)}`;
    }).join(' '),
    render: (value) => {
      return <AddSelectBilling viewOnly value={value} days={days} />
    }
  },
  {
    title: 'Internal Notes',
    dataIndex: 'intNotes',
    editable: true,
    width: 400,
    inputType: 'textarea',
    required: false,
  },
  {
    title: 'Pickup Route',
    dataIndex: 'pickupRoute',
    editable: true,
    options: opts.routeOptions,
    days,
    width: 400,
    required: false,
    customInput: p => <AddSelect {...p} />,
    // Route ids (`<routeId>_<rowId>`) mean nothing to a searcher — match the route
    // title the cell renders.
    searchValue: record => (get(record, 'pickupRoute') || []).map(r => {
      const route = get(r, 'route');
      const title = get((opts.routeOptions || []).find(o => o.value === route), 'title', route || 'Default');
      return `${title} ${get(r, 'day', '')}`;
    }).join(' '),
    render: (value) => {
      return <AddSelect viewOnly value={value} days={days} options={opts.routeOptions} />
    }
  },
  {
    title: 'Receiving Windows',
    dataIndex: 'windows',
    editable: true,
    inputType: 'TimePickerRange',
    required: false,
    width: 170,
    options: WINDOW_OPTIONS,
    // Only rows that have actually been given windows pay for the moment() formatting
    // here, so the global search stays cheap across the full license list.
    searchValue: record => {
      const wins = get(record, 'windows');
      if (!Array.isArray(wins)) { return ''; }
      return wins.map((wind, i) => {
        const label = get(WINDOW_OPTIONS, [i, 'label'], '');
        const hasStart = !!get(wind, 0);
        const hasEnd = !!get(wind, 1);
        const times = hasStart && hasEnd
          ? `${moment(get(wind, 0)).format(timeformat)} - ${moment(get(wind, 1)).format(timeformat)}`
          : 'N/A';
        return `${label}: ${times}`;
      }).join(' ');
    },
    render: function (text, record) {
      const wins = get(record, 'windows', defaultWindows);
      const windowsArr = Array.isArray(wins) ? wins : defaultWindows;
      return (
        <div>
          {windowsArr.map((wind, i) => {
            const hasStart = !!get(wind, 0);
            const hasEnd = !!get(wind, 1);
            return (
              <Row key={i} style={{ fontSize: 10 }}>
                <div style={{ marginRight: 5 }}>{`${get(WINDOW_OPTIONS, [i, 'label'])}: `}</div>
                <div>{hasStart && hasEnd ? `${moment(get(wind, 0)).format(timeformat)} - ${moment(get(wind, 1)).format(timeformat)}` : 'N/A'}</div>
              </Row>
            )
          })}
        </div>
      )
    }
  },
  {
    title: 'Active',
    dataIndex: 'active',
    inputType: 'singlecheckbox',
    editable: true,
    required: false,
    valuePropName: 'checked',
    render: (text, record) => {
      return (
        <span>
          {`${text}`}
        </span>
      )
    }
  },
  {
    // Read-only: scrape-derived, so it is deliberately not editable and not importable.
    title: 'State List',
    dataIndex: '_notOnStateList',
    required: false,
    width: 170,
    exportValue: record => stateListLabel(record),
    render: (text, record) => {
      const label = stateListLabel(record);
      if (!label) { return null; }
      return (
        <span style={{ color: get(record, '_notOnStateList') ? '#f5222d' : 'rgba(0, 0, 0, 0.45)' }}>
          {label}
        </span>
      )
    }
  },
  {
    title: 'Man. Req',
    dataIndex: 'manireq',
    inputType: 'singlecheckbox',
    editable: true,
    required: false,
    valuePropName: 'checked',
    render: (text, record) => {
      return (
        <span>
          {`${text}`}
        </span>
      )
    }
  },
  {
    // Card 286. Deliberately NOT `editable` — this is a one-click toggle rather than an
    // Edit/Save field, so an admin can lock a license without opening the row (and
    // without the row's required fields having to validate). Locking pins the five
    // protected values into lnstored as it flips the flag; see toggleOverride below.
    title: 'License Override',
    dataIndex: LICENSE_OVERRIDE_KEY,
    required: false,
    width: 130,
    searchValue: record => overrideLabel(record),
    exportValue: record => overrideLabel(record),
    render: (text, record) => {
      const locked = !!get(record, LICENSE_OVERRIDE_KEY);
      const ln = get(record, 'License #');
      // An unsaved "Add New LN" row has no license to hang the flag on yet.
      const disabled = !ln || !!record.isAdd;

      return (
        <Button
          outline={locked}
          disabled={disabled}
          hoverText={locked
            ? 'Locked — the license scrape will NOT overwrite Name, DBA, Street Address, City or Zip. Click to unlock.'
            : 'Unlocked — the license scrape WILL overwrite Name, DBA, Street Address, City and Zip. Click to lock.'}
          onClick={() => opts.onToggleOverride && opts.onToggleOverride(record)}
        >
          <Icon type={locked ? 'lock' : 'unlock'} />
        </Button>
      )
    }
  }
])

const ADDKEY = '000-00000';

export const LNUpdate = props => {
  const [state, setState] = useState({
    data: {},
    stored: {},
    loading: true,
    storedOnly: false,
    missingOnly: false,
    input: '',
    exporting: false,
    importing: false,
    importModalVisible: false,
    importData: [],
    routeOptions: [],
    total: 0,
    scrape: null,
  })

  const debInput = useDebounce(state.input, 750);

  const findMissingAddresses = () => {
    const lns = getKeyMaps('license', {});
    const storedMap = (state.stored && typeof state.stored === 'object' && !Array.isArray(state.stored)) ? state.stored : {};
    const keys = {};
    let total = 0; // merged non-deleted licenses, before the manual-edits filter and search

    // Search the same columns the grid renders (card 286). The merged row has to be
    // built BEFORE the search now — the old filter matched the raw scraped row, so a
    // stored override was invisible to it.
    const searchCols = COLS({ routeOptions: state.routeOptions });

    Object.keys(lns).forEach(k => {
      const ln = lns[k];
      const existsInStore = !!get(storedMap, k);

      if (get(storedMap, `${k}._deleted`)) { return; }
      if (!get(ln, 'License #')) { return; } // legacy junk rows with no LN
      total++;

      if (!existsInStore && state.storedOnly) { return; }
      if (state.missingOnly && !get(ln, '_notOnStateList')) { return; }

      const row = { ...getRawLicense(k), active: true, ...get(storedMap, k, {}) };
      if (!rowMatchesSearch(searchCols, row, state.input)) { return; }

      set(keys, k, row);
    })

    // Include stored-only licenses (e.g. newly imported) not in the license key maps
    Object.keys(storedMap).filter(k => !lns[k] && !get(storedMap, `${k}._deleted`)).forEach(k => {
      if (!k) { return; } // legacy junk entries with no LN key
      total++;

      if (state.missingOnly) { return; } // stored-only licenses are never state-sourced

      const row = { 'License #': k, active: true, ...get(storedMap, k, {}) };
      if (!rowMatchesSearch(searchCols, row, state.input)) { return; }

      set(keys, k, row);
    })

    setState(s => ({ ...s, loading: false, data: keys, total }));
  }

  const getStored = async () => {
    try {
      await window.LOADLICENSE();
      const [stored, routes, scrape] = await Promise.all([
        getStoredLicenseNumbers(),
        getRoutes(),
        // last-scrape banner is cosmetic — never let it fail the page load
        instance.get('/license/scrapestatus').then(r => get(r, 'data.data', null)).catch(() => null),
      ]);

      const routeOptions = [{ title: 'Default', value: null }];
      routes.map(r => {
        const rows = get(r, 'data.rows', []);

        rows.map(rr => {
          routeOptions.push({ title: `${r.name} - ${rr.city} - ${rr.zipCode}`, value: `${r.id}_${rr.id}` })
        })
      })

      setState(s => ({ ...s, stored, routeOptions, scrape }));
    } catch (err) {
      message.error(`Error: ${err.message}`);
    }
  }

  const updateData = async (_data, updatedRow, updatedIndex, action) => {
    try {
      const stored = { ...state.stored };
  
      const ln = get(updatedRow, 'License #');
  
      if (action === 'delete') {
        stored[ln] = { _deleted: true };
      } else {
        const isAdd = !!updatedRow.isAdd;
  
        if (isAdd) { //check to see if it already exists
          const exists = !!get(state, `data.${ln}`);
          if (exists) {
            setState(s => {
              const d = s.data;
              delete d[ADDKEY];
              return {
                ...s,
                data: d,
              }
            })
            throw new Error('License number already exists! Please try again!');
          } else {
            delete updatedRow['isAdd'];
          }
        }
  
        set(stored, ln, omit(updatedRow, SCRAPE_OWNED_KEYS));
      }

      const data = keyBy(_data, 'License #');
      setState(s => ({ ...s, data, stored }))

      await updateStoredLicenseNumbers(stored);
      getStored();
    } catch (err) {
      message.error(err.message);
    }
  };

  // Card 286 — flip the "License Override" lock on one license.
  //
  // Both halves of the lock land in lnstored (the user-owned map the scrape may not
  // clear): the flag itself, and — when locking — the row's CURRENT Name/DBA/Street
  // Address/City/Zip. Pinning matters because lnstored shadows the scraped blob key by
  // key: a license nobody has hand-edited has no stored Name at all, so blocking the
  // refresh alone would still let the next scrape's value show through the blob. Pinning
  // is what makes "the current value will not be overwritten" true for every row, not
  // just the previously-edited ones. Unlocking leaves the pinned values in place — they
  // are simply eligible for the scrape again, which is what "Unlock" is defined to mean.
  const toggleOverride = async record => {
    try {
      const ln = get(record, 'License #');
      if (!ln || record.isAdd) { return; }

      const locked = !get(record, LICENSE_OVERRIDE_KEY);
      const stored = { ...state.stored };
      const existing = get(stored, ln, {});

      const pinned = {};
      if (locked) {
        LOCK_PROTECTED_FIELDS.forEach(f => {
          const val = get(record, f);
          if (val !== undefined && val !== null && val !== '') { pinned[f] = val; }
        })
      }

      // Same strip as a normal row save: a merged grid row can carry the scrape-owned
      // state-list keys, and those must never be written into lnstored.
      set(stored, ln, omit({
        ...existing,
        ...pinned,
        'License #': ln,
        [LICENSE_OVERRIDE_KEY]: locked,
      }, SCRAPE_OWNED_KEYS));

      setState(s => ({
        ...s,
        stored,
        data: {
          ...s.data,
          [ln]: { ...get(s.data, ln, {}), ...pinned, [LICENSE_OVERRIDE_KEY]: locked },
        },
      }));

      await updateStoredLicenseNumbers(stored);
      message.success(`${ln} — license override ${locked ? 'locked' : 'unlocked'}`);
      getStored();
    } catch (err) {
      message.error(err.message);
    }
  };

  const changeText = e => {
    const input = e.target.value;
    setState(s => ({ ...s, input }));
  }

  const renderLastScrape = () => {
    const lastUpdated = get(state, 'scrape.lastUpdated');
    const lastScrapeDate = get(state, 'scrape.lastScrapeDate');
    const count = get(state, 'scrape.count', 0);
    const missing = get(state, 'scrape.missingCount', 0);

    if (!lastUpdated && !lastScrapeDate) { return 'Last scrape: —'; }

    const when = lastUpdated
      ? moment(lastUpdated).format('MMM D, YYYY h:mm a')
      : moment(lastScrapeDate, 'YYYY-MM-DD').format('MMM D, YYYY');
    return `Last scrape: ${when}${count ? ` — ${count} licenses retrieved` : ''}${missing ? ` — ${missing} no longer on state list` : ''}`;
  }

  const scrollTop = () => {
    try {
      document.getElementById('ContentContainer').scrollTop = 0;
    } catch (err) {
      console.log(err);
    }
  }

  const addRow = () => {
    setState(s => ({
      ...s,
      data: {
        [ADDKEY]: {
          'isAdd': true,
          'City': "",
          'DBA': "",
          'FacilityType': "",
          'License #': ADDKEY,
          'Licensee': "",
          'Street Address': "",
          'Zip': "",
        },
        ...s.data,
      }
    }))
    scrollTop();
    return ADDKEY;
  }

  const exportlns = async () => {
    try {
      setState(s => ({ ...s, exporting: true }))
      // Always export the full merged set (scraped + stored, stored fields win),
      // regardless of the on-screen filter/search — exports are used to reconcile
      // against METRC and must not silently inherit the view state.
      const lns = getKeyMaps('license', {});
      const all = {
        ...lns,
        ...state.stored,
      }

      const cols = COLS();
      const rows = [cols.map(c => c.title)];
      Object.keys(all).filter(k => !get(state, `stored.${k}._deleted`)).map(k => {
        // same junk guards as the view: scraped rows need a real LN, stored-only rows a real key
        if (lns[k] ? !get(lns[k], 'License #') : !k) { return; }
        const d = lns[k]
          ? { ...getRawLicense(k), active: true, ...get(state, `stored.${k}`, {}) }
          : { 'License #': k, active: true, ...get(state, `stored.${k}`, {}) };
        rows.push(cols.map(c => {
          if (typeof c.exportValue === 'function') { return c.exportValue(d); }
          const val = get(d, c.dataIndex, '');
          if (val === null || val === undefined || val === '') return '';
          if (typeof val === 'object') return JSON.stringify(val);
          return val.toString();
        }))
      })

      const ws = XLSX.utils.aoa_to_sheet(rows);
      // Force LN column (column 0) cells to text type so Excel/Numbers won't auto-convert to dates
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) {
          ws[addr].t = 's';
          ws[addr].z = '@';
        }
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'License Numbers');
      XLSX.writeFile(wb, `LN-export-${moment().format('YYYY-MM-DD')}.xlsx`);
    } catch (err) {
      message.error(err.message);
    } finally {
      setState(s => ({ ...s, exporting: false }))
    }
  }

  const HEADER_MAP = {
    'ln': 'License #',
    'license #': 'License #',
    'license': 'License #',
    'license number': 'License #',
    'name': 'Licensee',
    'licensee': 'Licensee',
    'dba': 'DBA',
    'street address': 'Street Address',
    'address': 'Street Address',
    'city': 'City',
    'zip': 'Zip',
    'zipcode': 'Zip',
    'zip code': 'Zip',
    'emails': 'emails',
    'email': 'emails',
    'internal notes': 'intNotes',
    'notes': 'intNotes',
    'active': 'active',
    'man. req': 'manireq',
    'manifest requirement': 'manireq',
    'billing': 'billing',
    'pickup route': 'pickupRoute',
    'receiving windows': 'windows',
    // Card 286 — keeps the grid's own export round-trippable. A sheet WITHOUT this
    // column leaves existing locks untouched (handleImport merges onto the stored row);
    // a blank cell in a sheet that HAS it reads as Unlocked, same as Active/Man. Req.
    'license override': LICENSE_OVERRIDE_KEY,
  };

  const JSON_COLUMNS = ['billing', 'pickupRoute', 'windows'];

  // Columns imported as booleans. Export writes the override column as Locked/Unlocked,
  // so 'locked' has to read as true alongside the usual true/1 spellings.
  const BOOLEAN_COLUMNS = ['active', 'manireq', LICENSE_OVERRIDE_KEY];

  const parseImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });

        if (!jsonData.length) {
          message.error('No data found in file');
          return;
        }

        // Map headers to our data indices
        const jsonWarnings = [];
        const mapped = jsonData.map((row, rowIndex) => {
          const mappedRow = {};
          Object.keys(row).forEach(key => {
            const normalizedKey = key.toLowerCase().trim();
            const mappedKey = HEADER_MAP[normalizedKey];
            if (mappedKey) {
              let val = row[key];
              // Always treat License # as a string to preserve leading zeros and formatting
              if (mappedKey === 'License #') {
                val = (val !== undefined && val !== null) ? val.toString().trim() : '';
              } else if (BOOLEAN_COLUMNS.includes(mappedKey)) {
                const boolStr = typeof val === 'string' ? val.toLowerCase().trim() : val;
                val = boolStr === true || boolStr === 'true' || boolStr === '1' || boolStr === 1 || boolStr === 'locked';
              } else if (JSON_COLUMNS.includes(mappedKey)) {
                if (typeof val === 'string' && val) {
                  try { val = JSON.parse(val); } catch (e) {
                    jsonWarnings.push({ row: rowIndex + 1, ln: row[Object.keys(row).find(k => (HEADER_MAP[k.toLowerCase().trim()] === 'License #'))] || `Row ${rowIndex + 1}`, field: key });
                    val = undefined;
                  }
                }
                if (val && !Array.isArray(val) && typeof val !== 'object') {
                  jsonWarnings.push({ row: rowIndex + 1, ln: row[Object.keys(row).find(k => (HEADER_MAP[k.toLowerCase().trim()] === 'License #'))] || `Row ${rowIndex + 1}`, field: key });
                  val = undefined;
                }
              }
              mappedRow[mappedKey] = val;
            }
          });
          return mappedRow;
        }).filter(row => !!row['License #']);

        if (jsonWarnings.length) {
          message.warning(`${jsonWarnings.length} field(s) had malformed data and were skipped: ${jsonWarnings.map(w => `${w.ln} (${w.field})`).join(', ')}`, 8);
        }

        if (!mapped.length) {
          message.error('No valid license numbers found. Ensure the file has a column named "LN", "License #", or "License Number".');
          return;
        }

        setState(s => ({ ...s, importData: mapped, importModalVisible: true }));
      } catch (err) {
        message.error(`Failed to parse file: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent default upload
  };

  const handleImport = async () => {
    try {
      setState(s => ({ ...s, importing: true }));
      const stored = { ...state.stored };

      state.importData.forEach(row => {
        const ln = get(row, 'License #', '').toString().trim();
        if (ln) {
          const existing = get(stored, ln, {});
          // Remove _deleted flag when re-importing a previously deleted license
          const { _deleted, ...rest } = existing;
          stored[ln] = {
            ...rest,
            ...row,
            'License #': ln,
          };
        }
      });

      await updateStoredLicenseNumbers(stored);
      message.success(`Successfully imported ${state.importData.length} license(s)`);
      setState(s => ({ ...s, importModalVisible: false, importData: [] }));
      getStored();
    } catch (err) {
      message.error(`Import failed: ${err.message}`);
    } finally {
      setState(s => ({ ...s, importing: false }));
    }
  };

  useEffect(() => {
    getStored();

    return () => {
      try {
        window.LOADLICENSES();
      } catch (err) {
        console.warn(err);
      }
    }
  }, []);

  useEffect(() => {
    setTimeout(findMissingAddresses, 200);
  }, [state.stored, state.storedOnly, state.missingOnly, debInput]);

  return (
    <div>
      <Alert message="DO NOT CHANGE ANYTHING ON THIS PAGE WITHOUT MANAGEMENT APPROVAL!!" type="error" style={{ marginBottom: 10 }} />
      <TableListHeader>
        <Input.Search placeholder="Search all columns" value={state.input} style={{ width: '40%', marginRight: 10 }} onChange={changeText} />
        <Checkbox checked={state.storedOnly} onChange={() => setState(s => ({ ...s, storedOnly: !s.storedOnly }))}>Only show licenses with manual edits</Checkbox>
        <Checkbox style={{ marginLeft: 16 }} checked={state.missingOnly} onChange={() => setState(s => ({ ...s, missingOnly: !s.missingOnly }))}>Only show licenses no longer on the state list</Checkbox>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Upload accept=".csv,.xlsx,.xls" showUploadList={false} beforeUpload={parseImportFile}>
            <Button type="primary" disabled={state.loading}>Import LN's</Button>
          </Upload>
          <Button type="primary" disabled={state.loading || state.exporting} onClick={exportlns}>Export LN's</Button>
        </div>
      </TableListHeader>
      <Row style={{ marginBottom: 10, gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'rgba(0, 0, 0, 0.65)' }}>
        {!state.loading && <span>{`Showing ${size(state.data)} of ${state.total} licenses`}</span>}
        <span>{renderLastScrape()}</span>
      </Row>
      <Modal
        title="Import License Numbers"
        visible={state.importModalVisible}
        onOk={handleImport}
        onCancel={() => setState(s => ({ ...s, importModalVisible: false, importData: [] }))}
        okText={`Import ${state.importData.length} License(s)`}
        confirmLoading={state.importing}
        width={700}
      >
        <p>The following <strong>{state.importData.length}</strong> license number(s) will be imported or updated:</p>
        <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e8e8e8', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>License #</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>DBA</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>City</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {state.importData.map((row, i) => {
                const ln = get(row, 'License #', '').toString().trim();
                const storedEntry = get(state, `stored.${ln}`);
                const existsInStored = !!storedEntry && !get(storedEntry, '_deleted');
                const regulatoryEntry = get(getKeyMaps('license', {}), ln);
                const existsInRegulatory = !!regulatoryEntry && !get(regulatoryEntry, '_deleted');
                const exists = existsInStored || existsInRegulatory;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #e8e8e8' }}>
                    <td style={{ padding: '6px 12px' }}>{row['License #']}</td>
                    <td style={{ padding: '6px 12px' }}>{row['Licensee'] || ''}</td>
                    <td style={{ padding: '6px 12px' }}>{row['DBA'] || ''}</td>
                    <td style={{ padding: '6px 12px' }}>{row['City'] || ''}</td>
                    <td style={{ padding: '6px 12px', color: exists ? '#faad14' : '#52c41a' }}>{exists ? 'Update' : 'New'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 16 }}>
          <Upload.Dragger accept=".csv,.xlsx,.xls" showUploadList={false} beforeUpload={parseImportFile}>
            <p><Icon type="inbox" style={{ fontSize: 32, color: '#40a9ff' }} /></p>
            <p>Drop a different file here to replace the current selection</p>
          </Upload.Dragger>
        </div>
      </Modal>
      <EditableFormTable
        data={Object.keys(state.data).map(k => state.data[k])}
        columns={withSorters(COLS({ routeOptions: state.routeOptions, onToggleOverride: toggleOverride }))}
        rowKey={'License #'}
        updateData={updateData}
        loading={state.loading}
        addRow={addRow}
        addTop
        addButton={'Add New LN'}
        tableProps={{ pagination: { hideOnSinglePage: true, pageSize: 50 } }}
      />
    </div>
  )
}
